import React, { useState } from 'react';

import { useFieldArray, useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import { useMutation, queryCache } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'src/common/components';
import { RemoteSelect } from 'src/common/components/Select';

import { createRequest, uploadPurchaseOrder } from '../api';
import {
  REQUESTER_TEAM_OPTIONS,
  REQUESTS_QUERY_KEY,
  REQUEST_TYPE_LABEL,
  REQUEST_TYPE_OPTIONS,
} from '../constants';
import { CreateRequestPayload, RequestType, RequesterTeam } from '../types';

import PhoneHardwareFields, { PhoneHardwareFormValues } from './PhoneHardwareFields';
import QueueEquipmentFields, { QueueEquipmentFormValues } from './QueueEquipmentFields';
import SipTrunkFields, { SipTrunkFormValues } from './SipTrunkFields';

type SharedFormValues = {
  accountName: string;
  requesterTeam: RequesterTeam;
  clientContactName: string;
  clientContactEmail: string;
  clientContactPhone: string;
  neededBy: string;
  notes: string;
};

export type RequestFormValues = SharedFormValues &
  SipTrunkFormValues &
  QueueEquipmentFormValues &
  PhoneHardwareFormValues;

type AccountOption = { value: string; label: string };

type NewRequestModalProps = {
  onClose: () => void;
};

// Two deliberately different conversions, named so the difference is visible at
// the call site: the backend's nullable columns want `null` when left blank,
// while its non-nullable counters want the 0 their model default implies.
const nullableNumber = (value: string): number | null => {
  const trimmed = value?.trim();
  return trimmed ? Number(trimmed) : null;
};

const countOrZero = (value: string): number => {
  const trimmed = value?.trim();
  return trimmed ? Number(trimmed) : 0;
};

const NewRequestModal: React.FC<NewRequestModalProps> = ({ onClose }: NewRequestModalProps) => {
  const navigate = useNavigate();
  const [requestType, setRequestType] = useState<RequestType>('SIP_TRUNK');
  const [account, setAccount] = useState<AccountOption | null>(null);
  const [purchaseOrderFile, setPurchaseOrderFile] = useState<File | null>(null);

  const methods = useForm<RequestFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      requesterTeam: 'SALES',
      reason: 'CLIENT_HAS_NO_TRUNK',
      installationType: 'SHIPPING',
      portingRequired: false,
      callRecordingRequired: false,
      networkReady: false,
      locations: '1',
      phoneLocations: [{ name: '', phoneCount: '1' }],
    },
  });

  const phoneLocationFields = useFieldArray({
    control: methods.control,
    name: 'phoneLocations',
  });

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    formState: { errors, dirtyFields },
  } = methods;

  const isSipTrunk = requestType === 'SIP_TRUNK';
  const isQueueEquipment = requestType === 'QUEUE_EQUIPMENT';
  const isPhoneHardware = requestType === 'PHONE_HARDWARE';

  const [submit, { isLoading }] = useMutation(
    async (values: RequestFormValues) => {
      const payload: CreateRequestPayload = {
        request_type: requestType,
        requester_team: values.requesterTeam,
        client_contact_name: values.clientContactName,
        client_contact_email: values.clientContactEmail,
        client_contact_phone: values.clientContactPhone,
        needed_by: values.neededBy || null,
        notes: values.notes,
      };

      if (account) payload.account = account.value;
      else payload.account_name = values.accountName;

      if (isSipTrunk) {
        payload.sip_trunk_detail = {
          reason: values.reason,
          reason_other: values.reasonOther,
          monthly_call_volume: nullableNumber(values.monthlyCallVolume),
          daily_call_volume: nullableNumber(values.dailyCallVolume),
          peak_concurrent_calls: countOrZero(values.peakConcurrentCalls),
          softphone_users: countOrZero(values.softphoneUsers),
          locations: countOrZero(values.locations),
          did_numbers_needed: countOrZero(values.didNumbersNeeded),
          porting_required: values.portingRequired,
          porting_details: values.portingDetails,
          call_recording_required: values.callRecordingRequired,
          expected_go_live: values.expectedGoLive || null,
        };
      } else if (isQueueEquipment) {
        payload.queue_equipment_detail = {
          locations_count: countOrZero(values.locationsCount),
          tv_count: countOrZero(values.tvCount),
          kiosk_count: countOrZero(values.kioskCount),
          tablet_count: countOrZero(values.tabletCount),
          pc_count: countOrZero(values.pcCount),
          purchase_order_number: values.purchaseOrderNumber,
          installation_type: values.installationType,
          installation_address: values.installationAddress,
          requested_installation_date: values.requestedInstallationDate || null,
          network_ready: values.networkReady,
        };
      } else {
        payload.phone_hardware_detail = {
          locations: values.phoneLocations.map((location) => ({
            name: location.name.trim(),
            phone_count: Number(location.phoneCount),
          })),
        };
      }

      const response = await createRequest(payload);

      // The PO document uploads separately so a failed attachment does not
      // throw away a request the backend already accepted and notified on.
      if (isQueueEquipment && purchaseOrderFile) {
        try {
          await uploadPurchaseOrder(response.data.id, purchaseOrderFile);
        } catch (error) {
          Toast.error('Request created, but the purchase order file failed to upload.');
        }
      }

      return response.data;
    },
    {
      onSuccess: (request) => {
        queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
        Toast.success('Request submitted. Tech support has been notified.');
        onClose();
        navigate(request.id);
      },
      onError: () => {
        Toast.error('There was an error submitting the request.');
      },
    }
  );

  return (
    <ReactModal
      isOpen
      onRequestClose={onClose}
      className="modal modal-md"
      overlayClassName="overlay"
      appElement={document.querySelector('#root') as HTMLElement}
    >
      {/* Not the shared `Modal`: its footer action closes unconditionally, which
          would dismiss the form before validation errors could be shown. */}
      <form
        onSubmit={handleSubmit((values) => submit(values))}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="flex-shrink-0">
          <h3>New provisioning request</h3>

          <div className="flex gap-2 mt-4">
            {REQUEST_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setRequestType(option.value)}
                className={
                  requestType === option.value
                    ? 'btn btn-secondary flex-1'
                    : 'btn btn-ghost border border-gray-300 flex-1'
                }
              >
                {REQUEST_TYPE_LABEL[option.value]}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0 pr-2 mt-4 -mr-2 overflow-y-auto">
          <fieldset>
            <legend className="form-legend">Client</legend>

            <label className="block mt-3 text-sm">
              Account
              <RemoteSelect
                url="account_admin?basic_info=true"
                dataPath="results"
                fieldId="id"
                fieldLabel="name"
                cacheOptions
                isClearable
                placeholder="Search accounts…"
                menuPortalTarget={document.body}
                onChange={(option: unknown) => setAccount(option as AccountOption | null)}
                // react-select drops what was typed the moment the field blurs.
                // For a prospect that is not in cosmos yet, that search text IS
                // the client name, so it is carried into the field below
                // instead of making the requester type it a second time. Only
                // while that field is untouched, so a name typed there survives
                // a later search.
                onInputChange={(typed: string, meta: { action: string }) => {
                  if (meta.action === 'input-change' && !dirtyFields.accountName) {
                    setValue('accountName', typed);
                  }
                }}
              />
            </label>

            {!account && (
              <label className="block mt-3 text-sm">
                Client name
                <input
                  className="input w-full"
                  placeholder="For prospects not yet in cosmos"
                  {...register('accountName', {
                    validate: (value) =>
                      !!value?.trim() || 'Pick an account or type the client name.',
                  })}
                />
                {errors.accountName && (
                  <span className="text-xs text-red-600">{errors.accountName.message}</span>
                )}
              </label>
            )}

            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="block text-sm">
                Requesting team
                <select className="input w-full" {...register('requesterTeam')}>
                  {REQUESTER_TEAM_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                Needed by
                <input type="date" className="input w-full" {...register('neededBy')} />
              </label>
              <label className="block text-sm">
                Client contact name
                <input className="input w-full" {...register('clientContactName')} />
              </label>
              <label className="block text-sm">
                Client contact email
                <input type="email" className="input w-full" {...register('clientContactEmail')} />
              </label>
              <label className="block text-sm">
                Client contact phone
                <input className="input w-full" {...register('clientContactPhone')} />
              </label>
            </div>
          </fieldset>

          {isSipTrunk && (
            <SipTrunkFields
              register={register}
              errors={errors}
              watch={watch}
              clearErrors={clearErrors}
            />
          )}
          {isQueueEquipment && (
            <QueueEquipmentFields
              register={register}
              errors={errors}
              watch={watch}
              clearErrors={clearErrors}
              purchaseOrderFile={purchaseOrderFile}
              onPurchaseOrderFileChange={setPurchaseOrderFile}
            />
          )}
          {isPhoneHardware && (
            <PhoneHardwareFields
              register={register}
              errors={errors}
              watch={watch}
              fieldArray={phoneLocationFields}
            />
          )}

          <label className="block mt-4 text-sm">
            {isPhoneHardware ? 'Additional notes' : 'Notes for tech support'}
            <textarea className="input w-full" rows={3} {...register('notes')} />
          </label>
        </div>

        <div className="flex flex-shrink-0 justify-end mt-5 pt-4 border-t border-white-2.0">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-secondary ml-4" disabled={isLoading}>
            {isLoading ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </form>
    </ReactModal>
  );
};

export default NewRequestModal;
